import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ConfirmPurchaseLoading } from '@app/components/ConfirmPurchaseLoading';
import { OrderError } from '@app/components/OrderError';
import StepNavigation, { Step } from '@app/components/StepNavigation';
import {
  purchaseAddOn,
  resetAddonConfiguration,
  resetEasyCareSelection,
  selectAddOns,
  selectCurrentEasyCareProduct,
  selectCurrentShopAddon,
  setEicMessageError,
  setErrorDisclosureCheck,
  setMissingSelection,
  setScrollToConsent,
} from '@app/redux/addOns';
import { selectConfig } from '@app/redux/config';

import GroupedAddonContent from '../../components/GroupedAddonContent';
import { OrderSuccess } from '../../components/OrderSuccess/OrderSuccess';
import Disclosure from '../Disclosure';

const GroupAddonNavigation = () => {
  const history = useHistory();
  const { t } = useTranslation('ShopAddons');
  const dispatch = useDispatch();
  const {
    submitOrder,
    eicChecked,
    configureAddonSuccess,
    getEicMessageError,
    addOnConsentRequirements,
    configureAddonError,
  } = useSelector(selectAddOns);
  const currentShopAddon = useSelector(selectCurrentShopAddon);
  const { name, offer_id } = currentShopAddon || {};

  const chosenEasyCareProduct = useSelector(selectCurrentEasyCareProduct);

  if (!currentShopAddon) {
    history.replace('/ShopAddons');
  }

  const purchasedProduct =
    currentShopAddon?.is_grouped_product && chosenEasyCareProduct ? chosenEasyCareProduct : currentShopAddon;

  const currentConsent =
    purchasedProduct && purchasedProduct.id && addOnConsentRequirements
      ? addOnConsentRequirements[purchasedProduct.id] ?? false
      : false;

  useEffect(() => {
    return () => {
      dispatch(resetEasyCareSelection());
      dispatch(resetAddonConfiguration());
    };
  }, [dispatch]);

  const steps: Step[] = useMemo(() => {
    const initialStep: Step = {
      title: `${name}`,
      component: GroupedAddonContent,
      current: true,
      displayCollapsibleCart: true,
      action: () => {
        if (!chosenEasyCareProduct?.id) {
          dispatch(setMissingSelection(true));
          return false;
        }
        if (!currentConsent) {
          dispatch(
            purchaseAddOn({
              productName: purchasedProduct?.name || '',
              productTypeId: purchasedProduct?.id || '',
              shoppingCartId: offer_id || '',
              rootProductTypeId: currentShopAddon?.id || '',
            })
          );
          return true;
        }

        return true;
      },
    };

    const stepsArray = [initialStep];

    if (currentConsent) {
      stepsArray.push({
        title: t('disclosure.titlePage'),
        component: Disclosure,
        current: false,
        displayCollapsibleCart: true,
        action: () => {
          if (!eicChecked) {
            dispatch(setErrorDisclosureCheck(true));
            dispatch(setScrollToConsent(true));
            return false;
          }

          if (configureAddonSuccess) {
            dispatch(
              purchaseAddOn({
                productName: purchasedProduct?.name || '',
                productTypeId: purchasedProduct?.id || '',
                shoppingCartId: offer_id || '',
                rootProductTypeId: currentShopAddon?.id || '',
              })
            );
          }
          return true;
        },
      });
    }

    return stepsArray;
  }, [
    name,
    offer_id,
    currentShopAddon?.id,
    purchasedProduct,
    chosenEasyCareProduct,
    currentConsent,
    configureAddonSuccess,
    eicChecked,
    dispatch,
    t,
  ]);

  // Handle error and loading states
  if (configureAddonError || getEicMessageError) {
    return <OrderError title={t('error.title')} handlePrimaryAction={() => dispatch(setEicMessageError(null))} />;
  }

  if (submitOrder.loading) {
    return <ConfirmPurchaseLoading title="confirmPurchase.title" description="confirmPurchase.description" />;
  }

  if (submitOrder.error) {
    return <OrderError title={t('error.title')} />;
  }

  if (submitOrder.success) {
    return <OrderSuccess />;
  }

  return <StepNavigation steps={steps} nameSpace="ShopAddons" />;
};

export default GroupAddonNavigation;
